import { useState } from 'react'
import { JSONTree } from 'react-json-tree'
import { Copy, Check } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

const theme = {
  scheme: 'context-inspector',
  author: 'agentic-platform',
  base00: '#09090b', // zinc-950
  base01: '#18181b', // zinc-900
  base02: '#27272a', // zinc-800
  base03: '#52525b', // zinc-600
  base04: '#a1a1aa', // zinc-400
  base05: '#d4d4d8', // zinc-300
  base06: '#e4e4e7', // zinc-200
  base07: '#fafafa', // zinc-50
  base08: '#f87171', // red-400
  base09: '#fb923c', // orange-400
  base0A: '#facc15', // yellow-400
  base0B: '#4ade80', // green-400
  base0C: '#22d3ee', // cyan-400
  base0D: '#60a5fa', // blue-400
  base0E: '#c084fc', // purple-400
  base0F: '#f472b6', // pink-400
}

export interface JsonTreeViewProps {
  data: unknown
  className?: string
}

export function JsonTreeView({ data, className }: JsonTreeViewProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(JSON.stringify(data, null, 2))
    setCopied(true)
    toast.success('JSON copied to clipboard')
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className={cn('rounded-lg border overflow-hidden', className)}>
      <div className="flex items-center justify-between px-4 py-2 bg-zinc-900 border-b border-zinc-800">
        <span className="text-xs text-zinc-400 font-mono">LLM Prompt Payload</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="h-7 text-xs text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 mr-1.5 text-green-400" />
          ) : (
            <Copy className="h-3.5 w-3.5 mr-1.5" />
          )}
          {copied ? 'Copied!' : 'Copy JSON'}
        </Button>
      </div>
      <div className="bg-[#09090b] p-4 text-sm">
        <JSONTree
          data={data}
          theme={theme}
          invertTheme={false}
          hideRoot
          shouldExpandNodeInitially={(_keyPath, _data, level) => level < 2}
        />
      </div>
    </div>
  )
}
